import {
  getCountryCallingCode,
  getExampleNumber,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

export type PhoneValidationError = "required" | "invalid" | "unsupported_country";

export type PhoneValidationResult =
  | { valid: true; e164: string }
  | { valid: false; error: PhoneValidationError };

const MAX_NATIONAL_DIGITS = 15;

export function isSupportedPhoneCountry(code: string): code is CountryCode {
  const upper = code.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(upper) && isSupportedCountry(upper);
}

export function sanitizeNationalInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, MAX_NATIONAL_DIGITS);
}

export function getDialCodeForCountry(country: string): string | null {
  if (!isSupportedPhoneCountry(country)) return null;
  try {
    return `+${getCountryCallingCode(country)}`;
  } catch {
    return null;
  }
}

/** National digits for the input field (strips E.164 to national when needed). */
export function getNationalForDisplay(phone: string, _country: string): string {
  void _country; // Retained for the existing public call signature; E.164 carries its own country.
  const trimmed = phone.trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("+")) {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (parsed?.nationalNumber) {
      return sanitizeNationalInput(parsed.nationalNumber);
    }
  }

  return sanitizeNationalInput(trimmed);
}

export function getPhonePlaceholder(country: string): string | undefined {
  if (!isSupportedPhoneCountry(country)) return undefined;
  try {
    const example = getExampleNumber(country, examples);
    return example?.formatNational();
  } catch {
    return undefined;
  }
}

export function validateNationalPhone(
  national: string,
  country: string,
): PhoneValidationResult {
  const digits = sanitizeNationalInput(national);
  if (!digits) {
    return { valid: false, error: "required" };
  }

  if (!isSupportedPhoneCountry(country)) {
    return { valid: false, error: "unsupported_country" };
  }

  const countryCode = country as CountryCode;

  const parsed = parsePhoneNumberFromString(digits, countryCode);
  if (parsed?.isValid()) {
    return { valid: true, e164: parsed.format("E.164") };
  }

  const withPlus = digits.startsWith("+") ? digits : `+${digits}`;
  if (isValidPhoneNumber(withPlus)) {
    const intl = parsePhoneNumberFromString(withPlus);
    if (intl?.isValid()) {
      return { valid: true, e164: intl.format("E.164") };
    }
  }

  if (isValidPhoneNumber(digits, countryCode)) {
    const fallback = parsePhoneNumberFromString(digits, countryCode);
    if (fallback?.isValid()) {
      return { valid: true, e164: fallback.format("E.164") };
    }
  }

  return { valid: false, error: "invalid" };
}

export type ParsedPrefillPhone = {
  country: CountryCode;
  national: string;
  e164?: string;
};

/** Parse URL/cache phone values into country + national digits. */
export function parsePrefilledPhone(
  value: string,
  defaultCountry: string = "US",
): ParsedPrefillPhone | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = parsePhoneNumberFromString(trimmed);
  if (parsed?.isValid() && parsed.country) {
    return {
      country: parsed.country,
      national: sanitizeNationalInput(parsed.nationalNumber),
      e164: parsed.format("E.164"),
    };
  }

  if (isSupportedPhoneCountry(defaultCountry)) {
    const national = sanitizeNationalInput(trimmed);
    if (national) {
      return { country: defaultCountry as CountryCode, national };
    }
  }

  return null;
}

/**
 * Normalizes phone/country from cache, URL prefill, or legacy drafts:
 * strips junk characters, parses E.164, and stores valid numbers as E.164.
 */
export function repairIntakePhoneFields(
  phone: string | undefined,
  country: string | undefined,
): { phone: string; country: string } {
  const defaultCountry =
    country?.trim() && isSupportedPhoneCountry(country)
      ? country.trim().toUpperCase()
      : "US";

  const raw = (phone ?? "").trim();
  if (!raw) {
    return { phone: "", country: defaultCountry };
  }

  const parsed = parsePrefilledPhone(raw, defaultCountry);
  if (parsed?.e164) {
    return { phone: parsed.e164, country: parsed.country };
  }

  const national = sanitizeNationalInput(raw);
  if (!national) {
    return { phone: "", country: defaultCountry };
  }

  const result = validateNationalPhone(national, defaultCountry);
  if (result.valid) {
    return { phone: result.e164, country: defaultCountry };
  }

  return { phone: national, country: defaultCountry };
}

export function formatPhoneForDisplay(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("+")) {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (parsed) {
      return parsed.formatInternational();
    }
  }

  return trimmed;
}
